/**
 * Venom 2.0 MMY routes
 *  - /make/model/newyear
 *  - /make/model/usedyear
 *  - /make/model/newyear/submodel
 *  - /make/model/usedyear/submodel
 *  - /make/model/
 *
 *  more about dynamic routes on https://gitlab.com/edmundscom/edmunds/node-site-venom/-/wikis/Routing-in-Venom#venom-20-routes
 */
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { getModelState } from 'client/data/luckdragon/model';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { UrlModel } from 'client/data/models/url';
import { buildVehiclePath, VehicleGraphQLModel } from 'client/data/models/vehicle-graphql';
import {
  VehicleDefaultModel,
  buildDefaultSubmodelPath,
  buildMakeModelDefaultYear,
} from 'client/data/models/vehicle-v2-default';
import { makes, TWO_WORDS_MAKE_NAMES_MAP } from 'client/data/constants/active-makes';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getQuery } from 'client/utils/location';
import { isUsedCoreMmyYear } from 'site-modules/shared/utils/core-page/used-core-mmy';
import { create404Error } from 'site-modules/shared/utils/errors';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { getLoadablePage } from 'site-modules/shared/components/loadable-page/loadable-page';

const MYCO_NEW_CORE_START_YEAR = 2024;

export const routes = [
  /**
   * New: new-core
   * Used: srp(<2022), used-core(>=2022)
   * [0]
   */
  {
    path: [`/:make(${makes.join('|')})/:model/:year`, `/:make(${makes.join('|')})/:model/:year/used`],
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      const isCore7696Enabled = store.getState()?.featureFlags?.['core-7696-vehicle-route-preload'];

      if (isCore7696Enabled) {
        preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);
      } else {
        preloader.resolve(buildDefaultSubmodelPath(params), VehicleDefaultModel);
      }
      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const isCore7696Enabled = modelState.get('core-7696-vehicle-route-preload', FeatureFlagModel);
      /* istanbul ignore next */
      const vehicleSubmodel = isCore7696Enabled
        ? modelState.get(buildVehiclePath(params), VehicleGraphQLModel)
        : modelState.get(buildDefaultSubmodelPath(params), VehicleDefaultModel);

      // TODO: Simplify to !!vehicleSubmodel if isCore7696Enabled set to default
      const hasVehicle = !!vehicleSubmodel?.id || !!vehicleSubmodel?.slug;
      if (!hasVehicle) {
        throw create404Error('Page not found');
      }

      const isUsedPubState = isUsed(vehicleSubmodel.pubStates);
      const isMycoNewCore = isUsedPubState && parseInt(params.year, 10) >= MYCO_NEW_CORE_START_YEAR;
      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsedPubState && !isMycoNewCore) {
        if (isUsedCoreMmyYear(params.year)) {
          const isCore7591Enabled =
            ExperimentUtil.getForcedOrAssignedRecipeName({
              state: store.getState(),
              campaignName: 'core-7591-used',
              defaultVal: 'ctrl',
            }) === 'chal';

          if (isCore7591Enabled) {
            return import(
              /* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition'
            ).then(page =>
              page.default({
                ...pageDefinitionParams,
                isMycoNewCore: true,
                isCore7591Enabled: true,
              })
            );
          }

          return import(
            /* webpackChunkName: "used_model_core" */ '../../core-page/pages/used-core/used-core-definition'
          ).then(page =>
            page.default({
              ...pageDefinitionParams,
              isUsed: true,
              isNoSubmodelPage: true,
            })
          );
        }
        return import(/* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition').then(
          page => page.default({ ...pageDefinitionParams, isSrpOnUsedCore: true })
        );
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default({ ...pageDefinitionParams, isMycoNewCore })
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  /**
   * New: model-price-checker
   * Used: srp
   * [1]
   */
  {
    path: `/:make(${makes.join('|')})/:model/:year/msrp`,
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);

      const isCore7696Enabled = store.getState()?.featureFlags?.['core-7696-vehicle-route-preload'];

      if (isCore7696Enabled) {
        preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);
      } else {
        preloader.resolve(buildDefaultSubmodelPath(params), VehicleDefaultModel);
      }

      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const isCore7696Enabled = modelState.get('core-7696-vehicle-route-preload', FeatureFlagModel);
      /* istanbul ignore next */
      const vehicle = isCore7696Enabled
        ? modelState.get(buildVehiclePath(params), VehicleGraphQLModel)
        : modelState.get(buildDefaultSubmodelPath(params), VehicleDefaultModel);

      // TODO: Simplify to !!vehicle if isCore7696Enabled set to default
      const hasVehicle = !!vehicle?.id || !!vehicle?.slug;
      if (!hasVehicle) {
        throw create404Error('Page not found');
      }
      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicle.pubStates)) {
        return import(/* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition').then(
          page => page.default({ ...pageDefinitionParams, isSrpOnUsedCore: true })
        );
      }

      const hasUtmParam = Object.keys(getQuery(store.getState().pageContext?.location)).some(
        key => key === 'utm_campaign'
      );

      if (hasUtmParam) {
        return import(
          /* webpackChunkName: "model-price-checker-social" */ '../../price-checker/pages/model-price-checker-social/model-price-checker-social-definition'
        );
      }

      return import(
        /* webpackChunkName: "model-price-checker" */ '../../price-checker/pages/model-price-checker/model-price-checker-definition'
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  /**
   * New: new-core
   * Used: used-core
   * [2]
   */
  {
    path: [`/:make(${makes.join('|')})/:model/:year/review`, `/:make(${makes.join('|')})/:model/:year/used/review`],
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      const isCore7696Enabled = store.getState()?.featureFlags?.['core-7696-vehicle-route-preload'];

      if (isCore7696Enabled) {
        preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);
      } else {
        preloader.resolve(buildDefaultSubmodelPath(params), VehicleDefaultModel);
      }

      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());

      const isCore7696Enabled = modelState.get('core-7696-vehicle-route-preload', FeatureFlagModel);
      /* istanbul ignore next */
      const vehicle = isCore7696Enabled
        ? modelState.get(buildVehiclePath(params), VehicleGraphQLModel)
        : modelState.get(buildDefaultSubmodelPath(params), VehicleDefaultModel);

      // TODO: Simplify to !!vehicle if isCore7696Enabled set to default
      const hasVehicle = !!vehicle?.id || !!vehicle?.slug;
      if (!hasVehicle) {
        throw create404Error('Page not found');
      }

      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicle.pubStates)) {
        const isCore7591Enabled =
          ExperimentUtil.getForcedOrAssignedRecipeName({
            state: store.getState(),
            campaignName: 'core-7591-used',
            defaultVal: 'ctrl',
          }) === 'chal';

        if (isCore7591Enabled) {
          return import(
            /* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition'
          ).then(page =>
            page.default({
              ...pageDefinitionParams,
              isMycoNewCore: true,
              isCore7591Enabled: true,
              isNoSubmodelPage: !params.submodel,
            })
          );
        }

        return import(
          /* webpackChunkName: "used_model_core" */ '../../core-page/pages/used-core/used-core-definition'
        ).then(page => page.default({ ...pageDefinitionParams, isUsed: true, isNoSubmodelPage: !params.submodel }));
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default(pageDefinitionParams)
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  {
    path: [
      `/:make(${makes.join('|')})/:model/:year/:submodel`,
      `/:make(${makes.join('|')})/:model/:year/:submodel/used`,
    ],
    exact: true,
    shouldCallParseApi: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      const isCore7696Enabled = store.getState()?.featureFlags?.['core-7696-vehicle-route-preload'];

      if (isCore7696Enabled) {
        preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);
      } else {
        preloader.resolve(buildDefaultSubmodelPath(params), VehicleDefaultModel);
      }

      return preloader.load();
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const isCore7696Enabled = modelState.get('core-7696-vehicle-route-preload', FeatureFlagModel);
      /* istanbul ignore next */
      const vehicle = isCore7696Enabled
        ? modelState.get(buildVehiclePath(params), VehicleGraphQLModel)
        : modelState.get(buildDefaultSubmodelPath(params), VehicleDefaultModel);

      // TODO: Simplify to !!vehicle if isCore7696Enabled set to default
      const hasVehicle = !!vehicle?.id || !!vehicle?.slug;
      if (!hasVehicle) {
        throw create404Error('Page not found');
      }

      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicle.pubStates)) {
        const isValidSrpUrl = modelState.get('context.isValidUrl', UrlModel);
        const isSupportedFilter = modelState.get('context.attributes.inventory.isSupportedFilter', UrlModel);
        // api/url/v3/parse publication state is cached for up to 1 week, so we need to check that it matches
        const publicationStateFromParseApi = modelState.get('context.vehicle.publicationState', UrlModel);

        if (isValidSrpUrl && isSupportedFilter && publicationStateFromParseApi === 'used') {
          return import(
            /* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition'
          ).then(page => page.default({ ...pageDefinitionParams, isSrpOnUsedCore: false }));
        }

        const isCore7591Enabled =
          ExperimentUtil.getForcedOrAssignedRecipeName({
            state: store.getState(),
            campaignName: 'core-7591-used',
            defaultVal: 'ctrl',
          }) === 'chal';

        if (isCore7591Enabled) {
          return import(
            /* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition'
          ).then(page =>
            page.default({
              ...pageDefinitionParams,
              isMycoNewCore: true,
              isCore7591Enabled: true,
              isNoSubmodelPage: !params.submodel,
            })
          );
        }

        return import(
          /* webpackChunkName: "used_model_core" */ '../../core-page/pages/used-core/used-core-definition'
        ).then(page => page.default({ ...pageDefinitionParams, isUsed: true, isNoSubmodelPage: !params.submodel }));
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default(pageDefinitionParams)
      );
    }),
    resolveLocationInParallelToPagePreload: true,
  },

  /**
   * without year
   * New: new-core
   * Used: srp
   * [3]
   */
  {
    path: `/:make(${makes.join('|')})/:model`,
    exact: true,
    preload: (params, store) => {
      const preloader = new ModelPreloader(store);
      const isCore7696Enabled = store.getState()?.featureFlags?.['core-7696-vehicle-route-preload'];

      if (isCore7696Enabled) {
        preloader.resolve(buildVehiclePath(params), VehicleGraphQLModel);
        return preloader.load();
      }

      preloader.resolve(buildMakeModelDefaultYear(params), VehicleDefaultModel);

      return preloader.load().then(modelState => {
        const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleDefaultModel);

        preloader.resolve(buildDefaultSubmodelPath({ ...params, year: defaultYear }), VehicleDefaultModel);

        return preloader.load();
      });
    },
    page: getLoadablePage((params, store) => {
      const modelState = getModelState(store.getState());
      const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleDefaultModel);

      const isCore7696Enabled = modelState.get('core-7696-vehicle-route-preload', FeatureFlagModel);
      /* istanbul ignore next */
      const vehicleSubmodel = isCore7696Enabled
        ? modelState.get(buildVehiclePath(params), VehicleGraphQLModel)
        : modelState.get(buildDefaultSubmodelPath({ ...params, year: defaultYear }), VehicleDefaultModel);

      // TODO: Simplify to !!vehicleSubmodel if isCore7696Enabled set to default
      const hasVehicle = !!vehicleSubmodel?.id || !!vehicleSubmodel?.slug;
      if (!hasVehicle) {
        throw create404Error('Page not found');
      }

      const pageDefinitionParams = {
        ...params,
        make: TWO_WORDS_MAKE_NAMES_MAP[params.make] || params.make,
      };

      if (isUsed(vehicleSubmodel.pubStates)) {
        return import(/* webpackChunkName: "used_srp_core" */ '../../inventory/pages/usurp/usurp-core-definition').then(
          page =>
            page.default({
              ...pageDefinitionParams,
              year: isCore7696Enabled ? vehicleSubmodel?.year : defaultYear,
              isSrpOnUsedCore: true,
            })
        );
      }

      return import(/* webpackChunkName: "new_model_core" */ '../../core-page/pages/new-core/new-core-definition').then(
        page => page.default(pageDefinitionParams)
      );
    }),
    resolveLocationInParallelToPagePreload: true,
    enableLooseEtag: true,
  },
];
